import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Stepper } from '@openedx/paragon';
import { FormattedMessage } from '@edx/frontend-platform/i18n';

import { FiltersProps } from '@src/library-authoring/library-filters';
import { PublishedFilterContextProvider } from '@src/library-authoring/common/context/PublishedFilterContext';
import {
  type ComponentSelectedEvent,
  type ComponentSelectionChangedEvent,
  ComponentPickerProvider,
} from '../common/context/ComponentPickerContext';
import { LibraryProvider, useOptionalLibraryContext } from '../common/context/LibraryContext';
import { SidebarProvider } from '../common/context/SidebarContext';
import LibraryAuthoringPage from '../LibraryAuthoringPage';
import LibraryCollectionPage from '../collections/LibraryCollectionPage';
import SelectLibrary from './SelectLibrary';
import messages from './messages';
import { ContentType, allLibraryPageTabs } from '../routes';

interface LibraryComponentPickerProps {
  returnToLibrarySelection?: () => void;
  visibleTabs: ContentType[],
  FiltersComponent?: React.ComponentType<FiltersProps>;
}

const InnerComponentPicker: React.FC<LibraryComponentPickerProps> = ({
  returnToLibrarySelection,
  visibleTabs,
  FiltersComponent,
}) => {
  const { collectionId } = useOptionalLibraryContext();

  if (collectionId) {
    return <LibraryCollectionPage />;
  }
  return (
    <LibraryAuthoringPage
      returnToLibrarySelection={returnToLibrarySelection}
      visibleTabs={visibleTabs}
      FiltersComponent={FiltersComponent}
    />
  );
};

/** Default handler in single-select mode. Used by the legacy UI for adding a single selected component to a course. */
const defaultComponentSelectedCallback: ComponentSelectedEvent = ({ usageKey, blockType }) => {
  window.parent.postMessage({ usageKey, type: 'pickerComponentSelected', category: blockType }, '*');
};

/** Default handler in multi-select mode. Used by the legacy UI for adding components to a problem bank. */
const defaultSelectionChangedCallback: ComponentSelectionChangedEvent = (selections) => {
  window.parent.postMessage({ type: 'pickerSelectionChanged', selections }, '*');
};

type PickerProps = {
  showOnlyPublished?: boolean,
  extraFilter?: string[],
  visibleTabs?: ContentType[],
  componentPickerMode?: 'single' | 'multiple',
  onComponentSelected?: ComponentSelectedEvent,
  onChangeComponentSelection?: ComponentSelectionChangedEvent,
  FiltersComponent?: React.ComponentType<FiltersProps>;
};

/** A component picker that allows the user to select one or more components */
export const ComponentPicker = ({
  /** Restrict the component picker to a specific library */
  libraryId,
  showOnlyPublished,
  extraFilter,
  componentPickerMode = 'single',
  visibleTabs = allLibraryPageTabs,
  /** This default callback is used to send the selected component back to the parent window,
   * when the component picker is used in an iframe.
   */
  onComponentSelected = defaultComponentSelectedCallback,
  onChangeComponentSelection = defaultSelectionChangedCallback,
  FiltersComponent,
  returnToLibrarySelection,
}: PickerProps & LibraryComponentPickerProps & { libraryId?: string }) => {
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const variant = queryParams.get('variant') || 'draft';
  const calcShowOnlyPublished = variant === 'published' || showOnlyPublished;

  const restrictToLibrary = !!libraryId;

  const componentPickerProviderProps = componentPickerMode === 'single' ? {
    componentPickerMode,
    onComponentSelected,
    restrictToLibrary,
  } : {
    componentPickerMode,
    onChangeComponentSelection,
    restrictToLibrary,
  };

  return (
    <PublishedFilterContextProvider showOnlyPublished={calcShowOnlyPublished}>
      <ComponentPickerProvider
        {...componentPickerProviderProps}
        extraFilter={extraFilter}
      >
        <SidebarProvider>
          { calcShowOnlyPublished
            && (
              <Alert variant="info" className="m-2">
                <FormattedMessage {...messages.pickerInfoBanner} />
              </Alert>
            )}
          <InnerComponentPicker
            returnToLibrarySelection={returnToLibrarySelection}
            visibleTabs={visibleTabs}
            FiltersComponent={FiltersComponent}
          />
        </SidebarProvider>
      </ComponentPickerProvider>
    </PublishedFilterContextProvider>
  );
};

/** A component picker that allows the user to select one or more components from a library. */
export const LibraryAndComponentPicker: React.FC<PickerProps> = ({
  showOnlyPublished,
  extraFilter,
  componentPickerMode = 'single',
  visibleTabs = allLibraryPageTabs,
  /** This default callback is used to send the selected component back to the parent window,
   * when the component picker is used in an iframe.
   */
  onComponentSelected = defaultComponentSelectedCallback,
  onChangeComponentSelection = defaultSelectionChangedCallback,
  FiltersComponent,
}) => {
  const [currentStep, setCurrentStep] = useState('select-library');
  const [selectedLibrary, setSelectedLibrary] = useState<string | undefined>(undefined);

  const handleLibrarySelection = (library: string) => {
    setSelectedLibrary(library);
    setCurrentStep('pick-components');
  };

  const returnToLibrarySelection = () => {
    setCurrentStep('select-library');
    setSelectedLibrary('');
  };

  return (
    <Stepper
      activeKey={currentStep}
    >
      <Stepper.Step eventKey="select-library" title="Select a library">
        <SelectLibrary
          selectedLibrary={selectedLibrary}
          setSelectedLibrary={handleLibrarySelection}
          itemType={visibleTabs.length === 1 ? visibleTabs[0] : ContentType.components}
        />
      </Stepper.Step>
      <Stepper.Step eventKey="pick-components" title="Pick some components">
        <LibraryProvider
          libraryId={selectedLibrary!}
          skipUrlUpdate
        >
          <ComponentPicker
            showOnlyPublished={showOnlyPublished}
            returnToLibrarySelection={returnToLibrarySelection}
            componentPickerMode={componentPickerMode}
            visibleTabs={visibleTabs}
            extraFilter={extraFilter}
            FiltersComponent={FiltersComponent}
            onComponentSelected={onComponentSelected}
            onChangeComponentSelection={onChangeComponentSelection}
          />
        </LibraryProvider>
      </Stepper.Step>
    </Stepper>
  );
};
